import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSeoTags } from '../utils/seoUtils';

/**
 * SEO component that handles all document head changes
 * - Generates proper hreflang tags for all supported languages
 * - Sets canonical URLs
 * - Adds appropriate meta tags
 */
const SEOHead = ({ title, description, keywords, image, type = 'website' }) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  
  // Get SEO tags for current path and language
  const { canonicalUrl, hreflangUrls } = getSeoTags(location.pathname, currentLanguage);
  
  // Default SEO content based on language
  const defaultSeo = {
    en: {
      title: 'TiDea Solis Yoga Studio - Yoga Classes and Workshops',
      description: 'Join our yoga classes, workshops, and events. Discover peace and wellness through yoga practice.',
      keywords: 'yoga, yoga classes, yoga workshops, wellness, meditation',
    },
    sk: {
      title: 'TiDea Solis Yoga Štúdio - Jogové kurzy a workshopy',
      description: 'Zúčastnite sa našich jogových hodín, workshopov a podujatí. Objavte pokoj a zdravie prostredníctvom jogy.',
      keywords: 'joga, jogové kurzy, jogové workshopy, wellness, meditácia',
    },
    hu: {
      title: 'TiDea Solis Jóga Stúdió - Jóga órák és workshopok',
      description: 'Csatlakozzon jóga óráinkhoz, workshopjainkhoz és eseményeinkhez. Fedezze fel a békét és a jóllétet a jóga gyakorlásán keresztül.',
      keywords: 'jóga, jóga órák, jóga workshopok, wellness, meditáció',
    }
  };
  
  // Use provided values or defaults
  const seoTitle = title || defaultSeo[currentLanguage]?.title || defaultSeo.en.title;
  const seoDescription = description || defaultSeo[currentLanguage]?.description || defaultSeo.en.description;
  const seoKeywords = keywords || defaultSeo[currentLanguage]?.keywords || defaultSeo.en.keywords;
  const seoImage = image || 'https://tideasolis.com/og-image.jpg';
  
  // Schema.org structured data
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "HealthAndBeautyBusiness",
    "name": "TiDea Solis Yoga Studio",
    "@id": "https://tideasolis.com",
    "url": "https://tideasolis.com",
    "logo": "https://tideasolis.com/logo.png",
    "image": seoImage,
    "description": currentLanguage === 'sk' ? 
      "Jogové štúdio ponúkajúce rôzne štýly jogy, workshopy a podujatia pre všetky úrovne." :
      currentLanguage === 'hu' ?
      "Jóga stúdió, amely különféle jóga stílusokat, workshopokat és eseményeket kínál minden szinten." :
      "Yoga studio offering various yoga styles, workshops, and events for all levels.",
    "telephone": "+421123456789",
    "email": "info@tideasolis.com",
    "areaServed": "Bratislava",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Example Street 123",
      "addressLocality": "Bratislava",
      "postalCode": "12345",
      "addressCountry": "SK"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "07:00",
        "closes": "21:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday", "Sunday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    ],
    "sameAs": [
      "https://facebook.com/tideasolisyoga",
      "https://instagram.com/tideasolisyoga"
    ]
  };
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLanguage} />
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang Tags */}
      {hreflangUrls.map(({ lang, url }) => (
        <link key={lang} rel="alternate" href={url} hreflang={lang} />
      ))}
      <link rel="alternate" href={hreflangUrls.find(item => item.lang === 'en')?.url} hreflang="x-default" />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:locale" content={
        currentLanguage === 'sk' ? 'sk_SK' : 
        currentLanguage === 'hu' ? 'hu_HU' : 'en_US'
      } />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
    </Helmet>
  );
};

export default SEOHead; 